import React, { Component } from "react";
import { Container } from "react-bootstrap";
import { withApollo } from "react-apollo";
import { withRouter } from "react-router-dom";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import ExpenseDashboard from "./ExpenseDashboard";
import ExpenseTypes from "./ExpenseTypes";
import AddExpense from "./AddExpense";
import VendorManagement from "./VendorManagement";
import PendingApprovals from "./PendingApprovals";
import { ExpensePageShimmer } from "./ExpenseShimmer";
import { attachModuleGuard, AccessDeniedModal, AccessDeniedCard } from "../../../utils/moduleGuard";

// /expense-management → "expense_management" (see MODULE_ROUTES in moduleAccessGuard).
// module_key ONLY, never module_name: the sidebar group name is shared by sibling
// modules, and can() grants an action when ANY matched row allows it — matching the
// group would let another module's grant leak in.
//
// The writes live in the tab children (ExpenseTypes, AddExpense, PendingApprovals), and
// each of those attaches its OWN guard rather than taking one as a prop — attachModuleGuard
// binds to the owning instance's setState, so a shared guard would have to render its modal
// here, and these children are unmounted/remounted per tab. Per-child guards keep each
// modal next to the action that raises it.
const EXPENSE_MODULE = ["expense_management"];

// ─── GraphQL Queries ──────────────────────────────────────────────────────
const GET_EXPENSE_CONFIGS = gql`
  query ($institute_id: Int!, $userId: Int!) {
    getExpenseHeads(institute_id: $institute_id) {
      id
      expense_head
      estatus
    }
    getExpenseCategories(institute_id: $institute_id) {
      id
      expense_categories
      expense_head_id
      description
      estatus
    }
    getExpenseVendors(institute_id: $institute_id) {
      id
      vendor_name
      contact_person
      contact_no
      address
      comments
      estatus
    }
    getPaymentModes {
      id
      payment_mode
      estatus
    }
    getExpenseUserPermission(userId: $userId) {
      isApprover
      employeeType
    }
  }
`;

const THEME = {
  bg: "#f5f7fa",
  bgCard: "#ffffff",
  primary: "#3b82f6",
  text: "#0f172a",
  textMuted: "#64748b",
  border: "#e2e8f0",
};

// URL slug for each tab — each section gets its own /expense-management/<tab> route
const VALID_TABS = ["dashboard", "add-expense", "expense-types", "pending-approvals"];

class ExpenseManagementMain extends Component {
  constructor(props) {
    super(props);
    this.state = {
      configs: null,
      editItem: null,
      loading: true,
      error: null,
      accessDeniedShow: false,
    };

    this.guard = attachModuleGuard(this, EXPENSE_MODULE);
  }

  componentDidMount() {
    // is_view === 0 → don't fetch the expense config. It's an imperative client.query()
    // (this module has no graphql HOCs at all), so bailing out here is what keeps the
    // data off the wire. render() returns Access Denied before it reads `loading`.
    if (!this.guard.canView()) return;

    this.fetchConfigs();
  }

  fetchConfigs = async () => {
    const { client } = this.props;
    const instituteId = parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || "0");
    const userId = parseInt(Cookies.get("id") || Cookies.get("user_id") || "0");

    try {
      const { data } = await client.query({
        query: GET_EXPENSE_CONFIGS,
        variables: { institute_id: instituteId, userId },
        fetchPolicy: "network-only"
      });
      this.setState({ configs: data, loading: false });
    } catch (err) {
      console.error("Error fetching expense configs:", err);
      this.setState({ error: err.message, loading: false });
    }
  };

  setActiveTab = (tab) => {
    // "Add Expense" is the Create action — opening it is the write's entry point. Validate
    // is_edit before navigating so a role without it gets the Access Denied modal instead
    // of being redirected into a form it can't submit. The tab stays visible + clickable;
    // only the navigation (redirect) is blocked. Dashboard / Categories & Vendors /
    // Approvals are reads and navigate freely.
    if (tab === "add-expense" && !this.guard.requireEdit()) return;

    if (tab !== "add-expense") this.setState({ editItem: null });
    this.props.history.push(`/expense-management/${tab}`);
  };

  // Row "edit" — routes into the AddExpense form. Gated before the push, otherwise
  // react-router would already have moved the user into the form. AddExpense re-checks
  // at its own submit (the tab can also be reached by URL).
  handleEdit = (item) => {
    if (!this.guard.requireEdit()) return;

    this.setState({ editItem: item });
    this.props.history.push("/expense-management/add-expense");
  };

  render() {
    // is_view === 0 → no expense data is rendered, and componentDidMount bails before
    // fetching it. Above the loading/error branches on purpose: with the fetch skipped,
    // `loading` stays true and the shimmer would spin forever.
    if (!this.guard.canView()) {
      return (
        <div style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)", minHeight: "100vh", padding: "24px 28px" }}>
          <AccessDeniedCard />
        </div>
      );
    }

    const { configs, loading, error } = this.state;
    const { client, match } = this.props;
    const tabParam = match && match.params && match.params.tab;
    const activeTab = VALID_TABS.includes(tabParam) ? tabParam : "dashboard";

    if (loading) {
      return <ExpensePageShimmer />;
    }

    if (error) {
      return (
        <div className="p-5 text-center">
          <h4 className="text-danger">Failed to load configuration</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={this.fetchConfigs}>Retry</button>
        </div>
      );
    }

    const tabs = [
      { key: "dashboard", label: "Dashboard", icon: "fal fa-tachometer-alt" },
      { key: "add-expense", label: "Add Expense", icon: "fal fa-plus-circle" },
      { key: "expense-types", label: "Categories & Vendors", icon: "fal fa-tags" },
      { key: "pending-approvals", label: "Approvals", icon: "fal fa-check-circle" },
    ];

    return (
      <div style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)", minHeight: "100vh", padding: "24px 28px" }}>
        <Container fluid className="px-0">
          <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.05)", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            
            {/* Unified full-page sub-header containing the Bootstrap nav-tabs */}
            <div style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", padding: "12px 24px 0 24px" }}>
              <ul className="nav nav-tabs" style={{ borderBottom: "none", gap: 8 }}>
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.key;
                  return (
                    <li className="nav-item" key={tab.key}>
                      <a
                        className={`nav-link ${isActive ? "active" : ""}`}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          this.setActiveTab(tab.key);
                        }}
                        style={{
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: 14,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "12px 20px",
                          color: isActive ? "#6366f1" : "#4b5563",
                          background: isActive ? "#fff" : "transparent",
                          borderColor: isActive ? "#e2e8f0 #e2e8f0 #fff" : "transparent",
                          borderWidth: "1.5px",
                          borderBottomWidth: isActive ? "2px" : "1.5px",
                          borderRadius: "10px 10px 0 0",
                          transition: "all 0.15s ease",
                          marginBottom: "-1.5px"
                        }}
                      >
                        <i className={tab.icon} />
                        {tab.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Seamless Content Panel without visual gaps and disjointed segments */}
            <div style={{ padding: "28px" }}>
              {activeTab === "dashboard" && (
                <ExpenseDashboard 
                  client={client}
                  configs={configs}
                  onEdit={this.handleEdit}
                />
              )}
              {activeTab === "add-expense" && (
                <AddExpense 
                  client={client}
                  configs={configs}
                  editItem={this.state.editItem}
                  onSuccess={() => this.setActiveTab("dashboard")}
                />
              )}
              {activeTab === "expense-types" && (
                <ExpenseTypes 
                  client={client}
                  configs={configs}
                  onUpdate={this.fetchConfigs}
                />
              )}
              {activeTab === "pending-approvals" && (
                <PendingApprovals 
                  client={client}
                  configs={configs}
                  onUpdate={this.fetchConfigs}
                />
              )}
            </div>

          </div>
        </Container>

        {/* Raised by handleEdit above. The tab children each own their own guard + modal
            for the writes that live inside them. */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.guard.hideAccessDenied}
        />
      </div>
    );
  }
}

export default withRouter(withApollo(ExpenseManagementMain));
